<script lang="ts">
    import { onMount } from 'svelte'
    import type { UpdateFormDataDto } from '../../modules/toolsApi/forms/form-datas/dto/update-form-data.dto'
    import { FormData } from '../../modules/toolsApi/forms/form-datas/form-data'
    import { FormDatasRepository } from '../../modules/toolsApi/forms/form-datas/form-datas.repository'
    import { FormDatasService } from '../../modules/toolsApi/forms/form-datas/form-datas.service'
    import CustomerServiceContent from '../customer-service/elements/customer-service-content.svelte'
    import { FormDataStatusEnum } from '../customer-service/form-data-status.enum'

    let formR: FormDatasRepository = new FormDatasRepository()
    let formS: FormDatasService = new FormDatasService(formR)
    let form: FormData = new FormData()

    let step = 0
    let companyId = null
    let formDataId = null

    // Formato novo (id=<linkId>_<token>, base58) — mesmo padrão do Forms V2. usingLink controla qual
    // método de update é chamado ao salvar; linkId/token só existem nesse formato.
    let usingLink = false
    let linkId = null
    let token = null

    let company = null

    function expandToken(id: string) {
        const parts = id.split('_')
        return {
            formDataLinkId: window.api.utils.conversions.base58ToUuid(parts[0]),
            token: window.api.utils.conversions.base58ToUuid(parts[1]),
        }
    }

    onMount(async () => {
        await load()
    })

    async function load() {
        const urlParams = new URLSearchParams(window.location.search)
        const id = urlParams.get('id')
        const rawLinkId = urlParams.get('linkId')
        // "t", não "token": "token" já é usado em toda a SPA para o JWT da sessão logada, e um usuário
        // autenticado abrindo este link público colidia com esse outro parâmetro.
        const rawToken = urlParams.get('t')
        if (id && id.includes('_')) {
            // Formato compartilhável (base58) — link copiado/enviado a uma pessoa.
            usingLink = true
            const expanded = expandToken(id)
            linkId = expanded.formDataLinkId
            token = expanded.token
            form = await formS.publicGetOneByLink(linkId, token)
        } else if (rawLinkId && rawToken) {
            // Formato cru (sem base58) — link de vida curta gerado internamente pra lambda-pdf
            // renderizar essa mesma página ao gerar o PDF assinado (ver form-data-signature.service.ts
            // no backend). Nunca é digitado/copiado por uma pessoa, então não precisa ser compacto.
            usingLink = true
            linkId = rawLinkId
            token = rawToken
            form = await formS.publicGetOneByLink(linkId, token)
        } else {
            // Formato antigo (cid/fid crus), mantido só por retrocompatibilidade com links já
            // enviados a clientes antes dessa mudança.
            companyId = urlParams.get('cid')
            formDataId = urlParams.get('fid')
            if (!formDataId) {
                step = 9
                return
            }
            form = await formS.publicGetOne(companyId, formDataId)
        }
        if (!form) {
            step = 10
            return
        }
        companyId = companyId || form.companyId
        window.api.users.statistics.tickPublicOnce(companyId, 'pub.form.open')
        company = await window.api.users.companies.getPublicCompany(companyId)
        step = 1
    }

    async function updateEntity(finished: boolean = false) {
        if (form.status === FormDataStatusEnum.SIGNED) return
        try {
            const dto: UpdateFormDataDto = {
                content: form.content,
                status: finished || form.status === FormDataStatusEnum.ACTIVE ? FormDataStatusEnum.ACTIVE : FormDataStatusEnum.TEMPORARY,
            }
            if (finished && !checkRequiredFields(dto)) {
                window.error('Todos os campos obrigatórios devem ser preenchidos')
                return false
            }
            if (usingLink) {
                await formS.publicUpdateByLink(linkId, token, dto)
            } else {
                await formS.publicUpdate(form.formDataId, companyId, dto)
            }
            // só o envio final conta como preenchido: o salvamento parcial (TEMPORARY) é rascunho
            if (finished) window.api.users.statistics.tickPublic(companyId, 'pub.form.fill')
            return true
        } catch (error) {
            window.error(error)
            return false
        } finally {
        }
    }

    function checkRequiredFields(dto: UpdateFormDataDto) {
        for (const item of dto.content) {
            if (item.type === 'label_formated') continue
            let field = form.template.form.find((el) => el.id === item.id)
            if (field && field.required) {
                if (item.type === 'text_short' || item.type === 'text_long') {
                    if (!item.value?.trim()) {
                        console.log('required 1', item)
                        return false
                    }
                }
                if (!item.another) {
                    if (Array.isArray(item.value)) {
                        if (item.value.length === 0) {
                            console.log('required 2', item)
                            return false
                        }
                    } else if (item.value instanceof Object) {
                        if (Object.keys(item.value).length === 0) {
                            console.log('required 3', item)
                            return false
                        }
                        if (!item.value?.trim()) {
                            console.log('required 3', item)
                            return false
                        }
                    }
                }
            } else if (item.type === 'sign') {
                if (item.value.length < 1000) {
                    console.log('required 4', item)
                    return false
                }
            }
        }
        return true
    }

    async function onNext() {
        await updateEntity(false)
    }

    async function onFinish() {
        if (await updateEntity(true)) {
            step = 2
        }
    }

    function customerServiceContentOnChange() {}
</script>

<div class="root cs_new">
    <div class="cs_new_content">
        {#if step === 0}
            <div class="d-flex justify-content-center align-items-center">Carregando...</div>
        {/if}
        {#if step === 1 && form}
            <CustomerServiceContent
                {company}
                isLink={true}
                view={form?.status !== FormDataStatusEnum.TEMPORARY}
                on:change={customerServiceContentOnChange}
                bind:formData={form}
            ></CustomerServiceContent>
            <div class="cs_new_public_save">
                <button on:click={onFinish} class="btn btn-primary">Salvar</button>
            </div>
        {/if}
        {#if step === 2}
            <div class="text-center mt-3">Formulário preenchido com sucesso. Obrigado pelo seu tempo.</div>
        {/if}
        {#if step === 9 || step === 10}
            <div class="text-center mt-3">Este documento não está disponível ({step}).</div>
        {/if}
    </div>
</div>

<style>
    :global(html) {
        overflow: unset !important;
    }
    :global(body) {
        overflow: unset !important;
    }
    .root {
        max-width: 970px;
        margin: 0 auto;
        font-weight: 400;
        padding: 6px;
    }
    .root :global(.input-group-text),
    .root :global(.form-control),
    .root :global(.input-background) {
        color: #000 !important;
        background-color: unset !important;
    }
    @media (max-width: 900px) {
        .root :global(.row .col) {
            padding-left: 0px !important;
            padding-right: 0px !important;
        }
    }
</style>
